import { ArrowUpRightIcon, TrashIcon } from "@heroicons/react/20/solid";
import { useMemo, useState } from "react";
import { SectionHeading } from "@/components/page";
import { Button, Field, UserChip } from "@/components/ui";
import { useFeedback } from "@/components/ui/feedback";
import { Modal } from "@/components/ui/Modal";
import { api } from "@/lib/api";
import { useProject } from "@/lib/project";
import type { Finance, MemberMoney } from "@/lib/types";
import { cn, formatDay, formatMoney, formatPercent, moneyInput, parseMoney, todayISO } from "@/lib/utils";
import { Money, MoneyInput, useFinanceRun } from "./common";

interface MemberPay extends MemberMoney {
  former: boolean;
  levelId: string | null;
  tasks: number;
  share: number;
  /** their parts of expenses, including what others covered for them */
  expenses: number;
}

const zeroMoney = (userId: string): MemberMoney => ({ userId, earned: 0, withdrawn: 0, paid: 0, owes: 0, owedToThem: 0, cash: 0, balance: 0, available: 0 });

function useMemberPay(fin: Finance): MemberPay[] {
  const { members } = useProject();
  return useMemo(() => {
    const money = new Map(fin.members.map((m) => [m.userId, m]));
    const current = new Map(fin.currentShares.map((m) => [m.userId, m]));
    const memberIds = new Set(members.map((m) => m.id));
    const ids = new Set(members.filter((m) => m.role !== "viewer" || money.has(m.id)).map((m) => m.id));
    for (const id of [...money.keys(), ...current.keys()]) ids.add(id);
    return [...ids]
      .map((userId) => {
        const m = money.get(userId) ?? zeroMoney(userId);
        return {
          ...m,
          former: !memberIds.has(userId),
          levelId: fin.memberLevels[userId] ?? null,
          tasks: current.get(userId)?.tasks ?? 0,
          share: current.get(userId)?.share ?? 0,
          expenses: m.earned - m.withdrawn - m.balance,
        };
      })
      .sort((a, b) => b.earned - a.earned || b.share - a.share);
  }, [fin, members]);
}

function PayoutDialog({ fin, member, onClose }: { fin: Finance; member: MemberPay; onClose: () => void }) {
  const { pid, userName } = useProject();
  const run = useFinanceRun();
  const [amount, setAmount] = useState(member.available > 0 ? moneyInput(member.available) : "");
  const [paidOn, setPaidOn] = useState(todayISO());
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const cents = parseMoney(amount);
  const tooMuch = cents !== null && cents > member.available;
  const currency = fin.settings.currency;

  return (
    <Modal
      open
      onClose={onClose}
      title={`Record a withdrawal for ${userName(member.userId)}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={saving}
            disabled={!cents || tooMuch || !paidOn}
            onClick={async () => {
              if (!cents) return;
              setSaving(true);
              const ok = await run(api.createPayout(pid, { userId: member.userId, amount: cents, paidOn, note }));
              setSaving(false);
              if (ok) onClose();
            }}
          >
            Record withdrawal
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-muted">
          Available to withdraw: <Money value={member.available} layout="inline" className="font-medium text-fg" />.
          {member.owedToThem > 0 && <> Money teammates still owe them isn't available until it's paid back.</>} Withdrawals lower the balance only; they
          don't change anyone's earned amount or share.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Amount" error={tooMuch ? "More than is available" : amount && cents === null ? "Enter a valid amount" : undefined}>
            <MoneyInput value={amount} onChange={setAmount} currency={currency} autoFocus invalid={tooMuch || (!!amount && cents === null)} />
          </Field>
          <Field label="Date">
            <input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} className="field" />
          </Field>
        </div>
        <Field label="Note">
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Bank transfer, cash…" className="field" dir="auto" />
        </Field>
      </div>
    </Modal>
  );
}

export function TeamTab({ fin }: { fin: Finance }) {
  const { pid, userName } = useProject();
  const run = useFinanceRun();
  const { confirm } = useFeedback();
  const rows = useMemberPay(fin);
  const [paying, setPaying] = useState<MemberPay | null>(null);
  const currency = fin.settings.currency;
  const totals = rows.reduce(
    (t, r) => ({ earned: t.earned + r.earned, withdrawn: t.withdrawn + r.withdrawn, expenses: t.expenses + r.expenses, balance: t.balance + r.balance }),
    { earned: 0, withdrawn: 0, expenses: 0, balance: 0 },
  );
  const debts = fin.debts.filter((d) => d.outstanding > 0);
  const expenseTitle = new Map(fin.expenses.map((e) => [e.id, e.title]));
  const levelById = new Map(fin.levels.map((l) => [l.id, l]));

  return (
    <div className="space-y-10">
      <section>
        <p className="mb-3 text-sm text-muted">
          <b className="font-medium text-fg">Earned</b> is locked each time money is received. <b className="font-medium text-fg">Current share</b> comes from finished tasks,
          their complexity and scope, and experience levels; it splits the next payment and every new expense. When someone can't pay their part of an
          expense, teammates cover it and their <b className="font-medium text-fg">Balance</b> goes negative until their next payment pays it back.
        </p>
        <div className="overflow-x-auto rounded-lg border border-line scroll-thin">
          <table className="w-full min-w-[760px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-line text-[13px] text-muted">
                <th className="px-3 py-1.5 text-start font-normal">Member</th>
                <th className="w-44 px-3 py-1.5 text-start font-normal">Experience</th>
                <th className="w-20 px-3 py-1.5 text-end font-normal">Done tasks</th>
                <th className="w-24 px-3 py-1.5 text-end font-normal">Current share</th>
                <th className="w-32 px-3 py-1.5 text-end font-normal">Earned</th>
                <th className="w-32 px-3 py-1.5 text-end font-normal">Withdrawn</th>
                <th className="w-32 px-3 py-1.5 text-end font-normal">Expenses</th>
                <th className="w-36 px-3 py-1.5 text-end font-normal">Balance</th>
                <th className="w-28" />
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {rows.map((r) => (
                <tr key={r.userId}>
                  <td className="px-3 py-2">
                    <UserChip id={r.userId} name={userName(r.userId)} className="max-w-full" />
                    {r.former && <div className="text-xs text-faint">No longer in the project</div>}
                  </td>
                  <td className="px-3 py-1.5">
                    {r.former ? (
                      <span className="text-faint">—</span>
                    ) : (
                      <select
                        value={r.levelId ?? ""}
                        onChange={(e) => run(api.setMemberLevel(pid, r.userId, e.target.value || null))}
                        className="field h-8 py-0"
                        aria-label={`Experience level for ${userName(r.userId)}`}
                      >
                        <option value="">No level (×1)</option>
                        {fin.levels.map((l) => (
                          <option key={l.id} value={l.id}>
                            {l.name} (×{l.multiplier})
                          </option>
                        ))}
                      </select>
                    )}
                    {r.levelId && !levelById.has(r.levelId) && <div className="text-xs text-faint">Unknown level</div>}
                  </td>
                  <td className="px-3 py-2 text-end text-muted tabular-nums">{r.tasks}</td>
                  <td className="px-3 py-2 text-end tabular-nums">{r.share > 0 ? formatPercent(r.share) : <span className="text-faint">—</span>}</td>
                  <td className="px-3 py-2 text-end">
                    <Money value={r.earned} />
                  </td>
                  <td className="px-3 py-2 text-end text-muted">
                    <Money value={r.withdrawn} />
                  </td>
                  <td className="px-3 py-2 text-end text-muted">
                    <Money value={r.expenses} />
                  </td>
                  <td className={cn("px-3 py-2 text-end font-semibold", r.balance < 0 && "text-danger")}>
                    <Money value={r.balance} />
                    {r.owes > 0 && <div className="text-[11px] font-normal text-danger">owes {formatMoney(r.owes, currency)}</div>}
                    {r.owedToThem > 0 && <div className="text-[11px] font-normal text-muted">{formatMoney(r.owedToThem, currency)} owed to them</div>}
                  </td>
                  <td className="px-2 text-end">
                    <Button size="sm" variant="ghost" icon={ArrowUpRightIcon} disabled={r.available <= 0} onClick={() => setPaying(r)}>
                      Withdraw
                    </Button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-3 py-6 text-center text-muted">
                    No members yet.
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className="border-t border-line font-medium">
                <td className="px-3 py-2" colSpan={4}>
                  Total
                </td>
                <td className="px-3 py-2 text-end">
                  <Money value={totals.earned} />
                </td>
                <td className="px-3 py-2 text-end">
                  <Money value={totals.withdrawn} />
                </td>
                <td className="px-3 py-2 text-end">
                  <Money value={totals.expenses} />
                </td>
                <td className="px-3 py-2 text-end">
                  <Money value={totals.balance} />
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      {debts.length > 0 && (
        <section>
          <SectionHeading>Who owes whom</SectionHeading>
          <p className="mb-2 text-sm text-muted">Paid back automatically from the member's cut of the next payments: teammates first, then the rest.</p>
          <div className="divide-y divide-line rounded-lg border border-line">
            {debts.map((d) => (
              <div key={d.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm">
                <UserChip id={d.debtor} name={userName(d.debtor)} className="min-w-28" />
                <span className="text-muted">{d.creditor ? "owes" : "still owes"}</span>
                {d.creditor ? <UserChip id={d.creditor} name={userName(d.creditor)} className="min-w-28" /> : <span className="text-muted">(nobody could cover it)</span>}
                <span className="min-w-0 flex-1 truncate text-xs text-faint" dir="auto">
                  for {expenseTitle.get(d.expenseId) ?? "an expense"}
                </span>
                <Money value={d.outstanding} layout="inline" className="font-medium" />
                {d.outstanding < d.amount && <span className="text-xs text-muted">of {formatMoney(d.amount, currency)}</span>}
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <SectionHeading>Withdrawals</SectionHeading>
        {fin.payouts.length === 0 ? (
          <p className="text-sm text-muted">No withdrawals yet. Use Withdraw on a member when you pay them out.</p>
        ) : (
          <div className="divide-y divide-line rounded-lg border border-line">
            {fin.payouts.map((o) => (
              <div key={o.id} className="group flex flex-wrap items-center gap-3 px-3 py-2 text-sm">
                <span className="w-28 text-muted">{formatDay(o.paidOn, true)}</span>
                <UserChip id={o.userId} name={userName(o.userId)} className="min-w-32 flex-1" />
                {o.note && (
                  <span className="min-w-0 flex-1 truncate text-muted" dir="auto">
                    {o.note}
                  </span>
                )}
                <Money value={o.amount} className="text-end font-medium" />
                <button
                  type="button"
                  className="rounded p-1 text-faint opacity-0 group-hover:opacity-100 hover:bg-hover hover:text-danger focus:opacity-100"
                  aria-label="Delete withdrawal"
                  onClick={async () => {
                    const ok = await confirm({
                      title: "Delete this withdrawal?",
                      message: `${formatMoney(o.amount, currency)} goes back to ${userName(o.userId)}'s balance.`,
                      confirmLabel: "Delete",
                      danger: true,
                    });
                    if (ok) run(api.deletePayout(pid, o.id));
                  }}
                >
                  <TrashIcon className="size-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
      {paying && <PayoutDialog fin={fin} member={paying} onClose={() => setPaying(null)} />}
    </div>
  );
}
